/**
 * Google Maps API統合 - 型定義
 * FL-35: Google Maps Distance Matrix API、Places API、Geocoding API統合
 */

/**
 * Google Maps Distance Matrix API の応答型
 * 引取先から搬入先までの距離・所要時間を表示
 */
export interface DistanceMatrixResult {
  /** 距離（km） */
  distance_km: number;

  /** 推定所要時間（分） */
  duration_minutes: number;

  /** API呼び出しステータス */
  status:
    | 'OK'
    | 'NOT_FOUND'
    | 'ZERO_RESULTS'
    | 'MAX_ROUTE_LENGTH_EXCEEDED'
    | 'REQUEST_DENIED'
    | 'OVER_QUERY_LIMIT'
    | 'UNKNOWN_ERROR';

  /** エラーメッセージ（ステータスがOK以外の場合） */
  error_message?: string;

  /** 生の距離（メートル） */
  distance_meters?: number;

  /** 生の所要時間（秒） */
  duration_seconds?: number;
}

/**
 * Google Maps Places API (Autocomplete - 旧版) の応答型
 * 住所入力時の自動補完候補
 * @deprecated Places API (New)への移行が推奨されています。PlaceAutocompleteResultNewを使用してください。
 */
export interface PlaceAutocompleteResult {
  /** 表示テキスト（ユーザーが見る住所） */
  description: string;

  /** Place ID（後続のジオコーディング等に使用） */
  place_id: string;

  /** マッチ箇所を示す配列 */
  matched_substrings?: Array<{
    length: number;
    offset: number;
  }>;

  /** 住所の主要な部分 */
  main_text?: string;

  /** 住所の副要素 */
  secondary_text?: string;

  /** 補完レベル（GEOCODE, CITY, etc） */
  types?: string[];
}

/**
 * Google Maps Places API (Autocomplete - 旧版) の応答ラッパー型
 * APIレスポンス全体
 * @deprecated Places API (New)への移行が推奨されています。PlaceAutocompleteResponseNewを使用してください。
 */
export interface PlaceAutocompleteResponse {
  /** 候補リスト */
  predictions: PlaceAutocompleteResult[];

  /** API呼び出しステータス */
  status:
    | 'OK'
    | 'ZERO_RESULTS'
    | 'INVALID_REQUEST'
    | 'OVER_QUERY_LIMIT'
    | 'REQUEST_DENIED'
    | 'UNKNOWN_ERROR';

  /** エラーメッセージ */
  error_message?: string;
}

/**
 * Google Maps Places API (Autocomplete - New) の候補型
 * 新APIのネストされた構造に対応
 */
export interface PlacePrediction {
  /** Place の完全な識別子 */
  placeId: string;

  /** 表示テキスト（ユーザーが見る住所） */
  text: {
    text: string;
    matches?: Array<{
      startOffset: number;
      endOffset: number;
    }>;
  };

  /** 補完形式（PLACE, QUERY） */
  structuredFormat?: {
    mainText?: {
      text: string;
      matches?: Array<{
        startOffset: number;
        endOffset: number;
      }>;
    };
    secondaryText?: {
      text: string;
      matches?: Array<{
        startOffset: number;
        endOffset: number;
      }>;
    };
  };

  /** Place タイプ */
  types?: string[];
}

/**
 * Google Maps Places API (Autocomplete - New) の応答型
 */
export interface PlaceAutocompleteResponseNew {
  /** 候補リスト */
  suggestions: Array<{
    placePrediction: PlacePrediction;
  }>;
}

/**
 * Google Maps Geocoding API の応答型
 * 住所を座標に変換
 */
export interface GeocodingResult {
  /** 緯度 */
  latitude: number;

  /** 経度 */
  longitude: number;

  /** フォーマット済み住所 */
  formatted_address: string;

  /** 住所の構成要素 */
  address_components?: Array<{
    long_name: string;
    short_name: string;
    types: string[];
  }>;

  /** 地図上の位置のタイプ（ROOFTOP, RANGE_INTERPOLATED, GEOMETRIC_CENTER, APPROXIMATE） */
  location_type?: string;

  /** API呼び出しステータス */
  status:
    | 'OK'
    | 'ZERO_RESULTS'
    | 'OVER_QUERY_LIMIT'
    | 'REQUEST_DENIED'
    | 'INVALID_REQUEST'
    | 'UNKNOWN_ERROR';

  /** エラーメッセージ */
  error_message?: string;
}

/**
 * 距離計算リクエストの型
 * CaseFormPage→googleMapsService への入力パラメータ
 */
export interface DistanceCalculationRequest {
  /** 引取先住所 */
  pickup_address: string;

  /** 搬入先住所 */
  delivery_address: string;
}

/**
 * 距離計算結果を案件に保存する型
 * googleMapsService→Supabase への出力
 */
export interface CaseDistanceData {
  /** 距離（km） */
  distance_km: number;

  /** 推定所要時間（分） */
  estimated_duration_minutes: number;

  /** 距離計算実行日時 */
  distance_calculated_at: string; // ISO 8601 format
}

/**
 * Google Maps サービス層のエラー型
 */
export class GoogleMapsServiceError extends Error {
  code: string;
  originalError?: Error;

  constructor(code: string, message: string, originalError?: Error) {
    super(message);
    this.name = 'GoogleMapsServiceError';
    this.code = code;
    this.originalError = originalError;
  }
}

/**
 * API_KEY 検証結果の型
 */
export interface ApiKeyValidationResult {
  /** API_KEYが設定されているか */
  isConfigured: boolean;

  /** API_KEYの形式が正しいか */
  isValidFormat: boolean;

  /** 検証エラーメッセージ */
  errorMessage?: string;
}

/**
 * Distance Matrix API の レート制限情報
 */
export interface RateLimitInfo {
  /** 1秒あたりの最大リクエスト数 */
  requests_per_second: number;

  /** 1日あたりの最大リクエスト数 */
  requests_per_day: number;

  /** 無料枠での最大金額（月） */
  free_tier_limit_usd: number;
}

/**
 * Distance Matrix API キャッシュキー
 */
export interface DistanceCacheKey {
  /** 引取先住所（正規化済み） */
  pickup_address_normalized: string;

  /** 搬入先住所（正規化済み） */
  delivery_address_normalized: string;

  /** キャッシュの有効期間（日数） */
  cache_validity_days: number;
}

/**
 * 距離計算のレスポンスステータス
 */
export type DistanceCalculationStatus =
  | 'success'
  | 'error'
  | 'not_found'
  | 'rate_limited'
  | 'cancelled';

/**
 * 距離計算処理の進行状況
 */
export interface DistanceCalculationProgress {
  /** ステータス */
  status: DistanceCalculationStatus;

  /** 進捗率（0-100） */
  progress: number;

  /** メッセージ */
  message: string;

  /** 計算結果（成功時のみ） */
  result?: CaseDistanceData;

  /** エラー情報（失敗時のみ） */
  error?: {
    code: string;
    message: string;
  };
}

/**
 * Google Maps Directions API のルートステップ（個別の移動指示）
 * 例：「●メートル進む」「右折」など
 */
export interface RouteStep {
  /** 移動距離（メートル、value フィールド含むオブジェクト） */
  distance?: {
    value: number; // メートル単位
    text: string; // フォーマット済みテキスト（例："1.5 km"）
  };

  /** 所要時間（秒、value フィールド含むオブジェクト） */
  duration?: {
    value: number; // 秒単位
    text: string; // フォーマット済みテキスト（例："3 mins"）
  };

  /** HTML形式の移動指示テキスト */
  html_instructions: string;

  /** テキスト形式の移動指示テキスト */
  text_instructions?: string;

  /** 開始地点の座標 */
  start_location?: {
    lat: number;
    lng: number;
  };

  /** 終了地点の座標 */
  end_location?: {
    lat: number;
    lng: number;
  };
}

/**
 * Google Maps Directions API のルート脚（1つの経路セグメント）
 * 例：引取先→経由地1、経由地1→経由地2、経由地2→搬入先
 */
export interface RouteLeg {
  /** 開始地点の住所 */
  start_address: string;

  /** 終了地点の住所 */
  end_address: string;

  /** 距離（メートル、value フィールド含むオブジェクト） */
  distance?: {
    value: number; // メートル単位
    text: string; // フォーマット済みテキスト（例："123 km"）
  };

  /** 所要時間（秒、value フィールド含むオブジェクト） */
  duration?: {
    value: number; // 秒単位
    text: string; // フォーマット済みテキスト（例："2 hours"）
  };

  /** トラフィック考慮後の所要時間（秒、trafficModel有効時） */
  duration_in_traffic?: {
    value: number; // 秒単位
    text: string; // フォーマット済みテキスト
  };

  /** 移動ステップの配列 */
  steps: RouteStep[];

  /** トラフィック状況の配列 */
  traffic_speed_entries?: Array<{
    distance: number;
    duration: number;
  }>;
}

/**
 * Google Maps Directions API のルート情報
 * 複数の脚から成り立つ完全なルート
 */
export interface DirectionRoute {
  /** 開始地点 */
  start_address: string;

  /** 終了地点 */
  end_address: string;

  /** 総距離（メートル、value フィールド含むオブジェクト） */
  distance?: {
    value: number; // メートル単位
    text: string; // フォーマット済みテキスト（例："123 km"）
  };

  /** 総所要時間（秒、value フィールド含むオブジェクト） */
  duration?: {
    value: number; // 秒単位
    text: string; // フォーマット済みテキスト（例："2 hours"）
  };

  /** トラフィック考慮後の所要時間（秒、trafficModel有効時） */
  duration_in_traffic?: {
    value: number; // 秒単位
    text: string; // フォーマット済みテキスト
  };

  /** ルートの脚（複数の経由地を含む場合は複数） */
  legs: RouteLeg[];

  /** ポリラインオブジェクト（encoded: 圧縮されたポリライン） */
  polyline?: {
    points: string; // エンコードされたポリラインポイント
  };

  /** ウェイポイント（複数経由地がある場合） */
  waypoint_order?: number[];

  /** ウェイポイント住所 */
  waypoint_addresses?: string[];
}

/**
 * Google Maps Directions API の応答型
 */
export interface DirectionResult {
  /** ルート情報の配列（通常は最初の要素を使用） */
  routes: DirectionRoute[];

  /** API呼び出しステータス */
  status:
    | 'OK'
    | 'NOT_FOUND'
    | 'ZERO_RESULTS'
    | 'MAX_ROUTE_LENGTH_EXCEEDED'
    | 'REQUEST_DENIED'
    | 'OVER_QUERY_LIMIT'
    | 'INVALID_REQUEST'
    | 'UNKNOWN_ERROR';

  /** エラーメッセージ */
  error_message?: string;
}

/**
 * ルート可視化用のデータ
 * UI表示に最適化された形式
 */
export interface RouteVisualizationData {
  /** 引取先住所 */
  pickup_address: string;

  /** 搬入先住所 */
  delivery_address: string;

  /** 距離（km） */
  distance_km: number;

  /** 所要時間（分） */
  estimated_duration_minutes: number;

  /** トラフィック考慮後の所要時間（分、オプション） */
  duration_in_traffic_minutes?: number;

  /** ポリラインポイント（緯度経度配列） */
  polyline_points: Array<{ lat: number; lng: number }>;

  /** トラフィック状況（'light' | 'moderate' | 'heavy'） */
  traffic_level?: 'light' | 'moderate' | 'heavy';

  /** ルート計算実行日時 */
  calculated_at: string; // ISO 8601 format

  /** ルートの詳細ステップ */
  steps: Array<{
    instruction: string;
    distance_meters: number;
    duration_seconds: number;
  }>;

  /** 距離データの出典（キャッシュ/API/期限切れキャッシュ） */
  distance_source?: 'cache' | 'api' | 'expired-cache';
}

/**
 * Directions API リクエスト用オプション
 */
export interface DirectionsRequestOptions {
  /** 経由地アドレスの配列 */
  waypointAddresses?: string[];

  /** トラフィックモデル ('best_guess' | 'pessimistic' | 'optimistic') */
  trafficModel?: 'best_guess' | 'pessimistic' | 'optimistic';

  /** 出発日時（Unix timestamp、秒） */
  departureTime?: number;

  /** 言語コード（デフォルト: 'ja'） */
  language?: string;

  /** ユニット（'metric' | 'imperial'） */
  units?: 'metric' | 'imperial';

  /** 移動手段（'driving' | 'transit' | 'walking' | 'bicycling'） */
  mode?: 'driving' | 'transit' | 'walking' | 'bicycling';
}

/**
 * 最寄駅検索結果の型（FL-270）
 * Google Maps Places Autocomplete API からの候補
 */
export interface StationResult {
  /** 駅名（ユーザーが見るテキスト） */
  name: string;

  /** Place ID（詳細情報取得に使用） */
  placeId: string;

  /** 駅の種類（train_station, subway_station など） */
  types?: string[];

  /** 副テキスト（都道府県など） */
  secondaryText?: string;
}

/**
 * 最寄駅の詳細情報型（FL-270）
 * Google Maps Geocoding API から取得した緯度経度を含む
 */
export interface StationDetails {
  /** 駅名 */
  name: string;

  /** Google Maps Place ID */
  placeId: string;

  /** 緯度 */
  latitude: number;

  /** 経度 */
  longitude: number;

  /** フォーマット済み住所 */
  formattedAddress: string;

  /** 住所の構成要素 */
  addressComponents?: Array<{
    longName: string;
    shortName: string;
    types: string[];
  }>;

  /** API呼び出しステータス */
  status:
    | 'OK'
    | 'ZERO_RESULTS'
    | 'OVER_QUERY_LIMIT'
    | 'REQUEST_DENIED'
    | 'INVALID_REQUEST'
    | 'UNKNOWN_ERROR';

  /** エラーメッセージ（ステータスがOK以外の場合） */
  error_message?: string;
}
